import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

// import { Button } from '@ui/Components/common/shared/Button'
import { Spinner } from '@ui/components/loaders'
import { KeyValue, NodeError } from '@ui/interfaces'
import { GetPipelineHistoryQuery } from '@ui/loaders/processor'
import { ViewErrorModal } from '@ui/pages/pipelines/components'
import clsx from 'clsx'
import dayjs from 'dayjs'

export function PipelineErrors() {
  const [openModal, setOpenModal] = useState(false)
  const [scrollPosition, setScrollPosition] = useState(0)

  const [selectedError, setSelectedError] = useState<NodeError | null>(null)
  const [fetchedData, setFetchedData] = useState<NodeError[]>([])
  const [currentPage] = useState(1)
  const [fetchLimit] = useState(100)

  const tableRef = useRef<HTMLDivElement>(null)

  const navigate = useNavigate()

  if (!useParams().pipelineId) {
    navigate('/pipelines')
  }

  const { pipelineId } = useParams()

  const {
    data,
    loading = true,
    // fetchMore,
  } = GetPipelineHistoryQuery(pipelineId as string, true, fetchLimit, currentPage)

  useEffect(() => {
    if (data) {
      setFetchedData(data.pipelineHistory)
    }
  }, [data])

  if (loading || !data) {
    return (
      <div className="container flex h-[80dvh] items-center justify-center rounded-md bg-white p-5">
        <Spinner />
      </div>
    )
  }

  if (fetchedData.length === 0) {
    return
  }

  // const getPage = (direction: 'previous' | 'next') => {
  //   switch (direction) {
  //     case 'previous':
  //       if (currentPage === 1) return
  //       setCurrentPage(currentPage - 1)
  //       fetchMore({
  //         variables: {
  //           offset: (currentPage - 2) * fetchLimit,
  //         },
  //       })
  //       break
  //     case 'next':
  //       if (fetchedData.length < fetchLimit) return
  //       setCurrentPage(currentPage + 1)
  //       fetchMore({
  //         variables: {
  //           offset: currentPage * fetchLimit,
  //         },
  //       })
  //       break
  //   }
  // }

  const selectError = (error: NodeError) => {
    setSelectedError(error)
    setOpenModal(true)
  }

  const closeModal = () => {
    setSelectedError(null)
    setOpenModal(false)
  }

  const handleScroll = () => {
    if (!tableRef.current) return
    const position = tableRef.current.scrollTop
    setScrollPosition(position)
  }
  const order: KeyValue = {
    nodeType: 'Type',
    description: 'Description',
    firstSeen: 'First seen',
    lastSeen: 'Last seen',
    lastDay: '30 min count',
    errorMessage: 'Error message',
  }

  const getTableHeadersInOrder = () => {
    return Object.keys(order).map((header: string) => {
      // const firstElement = index === 0
      // const lastElement = index === Object.keys(order).length - 1
      const humanReadableHeader = order[header]

      return (
        <th
          className={clsx(
            'sticky top-0 z-10 border border-gray-300 py-2.5 pl-5 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3',
            {
              'shadow-lg': scrollPosition > 0,
            }
          )}
          key={header}>
          {humanReadableHeader}
        </th>
      )
    })
  }

  const getTableRowsInOrder = (data: NodeError) => {
    return Object.keys(order).map((header) => {
      switch (order[header]) {
        case 'First seen':
          return (
            <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900" key={header}>
              {dayjs(data[header] as Date).format('L LT')}
            </td>
          )
        case 'Last seen':
          return (
            <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900" key={header}>
              {dayjs(data[header] as Date).fromNow()}
            </td>
          )
        case '30 min count':
          return (
            <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900" key={header}>
              {data[header].toLocaleString()}
            </td>
          )
        case 'lastDay':
          return (
            <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900" key={header}>
              {data[header].toLocaleString()}
            </td>
          )
        default:
          return (
            <td
              className={clsx('h-5 border border-gray-300 px-4 py-2 text-sm text-gray-900', {
                'justify-center': (header === data[header]) === null,
              })}
              key={header}>
              {data[header] === null ? '-' : (data[header] as string)}
            </td>
          )
      }
    })
  }

  return (
    <>
      <ViewErrorModal error={selectedError} open={openModal} setOpen={closeModal} />
      <div className="h-[calc(100vh-5rem)] rounded-md border border-gray-200 bg-white p-5">
        <div className="flex flex-row justify-between">
          <p className="my-2.5">
            Displaying <b className="text-gray-600">{fetchLimit * (currentPage - 1) + fetchedData.length}</b> unique
            errors
            {/* <b className="text-gray-600">{fetchedData[0].totalRuns}</b> errors */}
          </p>

          <div className="flex flex-row">
            {/* <Button onClick={() => getPage('previous')} disabled={currentPage === 1}>
              Previous
            </Button>
            <Button onClick={() => getPage('next')} disabled={fetchedData.length < fetchLimit} addMarginX={false}>
              Next
            </Button> */}
          </div>
        </div>

        <div className="max-h-full overflow-scroll" ref={tableRef} onScroll={handleScroll}>
          <table className="h-full min-w-full">
            <thead className="sticky top-0 z-10 bg-white text-left text-sm font-semibold text-gray-900 sm:pl-3">
              <tr>{getTableHeadersInOrder()}</tr>
            </thead>
            <tbody>
              {fetchedData.map((row: NodeError, index: number) => (
                <tr
                  className={clsx('cursor-pointer text-left transition duration-100 hover:bg-gray-300', {
                    'bg-gray-100': index % 2 === 0,
                    'bg-white': index % 2 !== 0,
                  })}
                  key={row.id}
                  onClick={() => selectError(row)}>
                  {getTableRowsInOrder(row)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
